import styles from "./Markdown.module.css";
import { Box, Code, ScrollArea, Table } from "@mantine/core";
import type { ReactNode } from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";

/** The default `a` renderer: opens an external link in a new tab, keeps an in-page anchor
 * (an `href` starting with `#`) from doing so. Exported so a caller that needs to render
 * some links specially (e.g. a report's citation-aware link) can fall back to this for
 * everything else. */
export function DefaultMarkdownLink({ href, children, id }: { href?: string; children?: ReactNode; id?: string }) {
  return (
    <a href={href} id={id} {...(href?.startsWith("#") ? {} : { target: "_blank", rel: "noopener noreferrer" })}>
      {children}
    </a>
  );
}

const markdownComponents = {
  a: DefaultMarkdownLink,
  code: ({ children }: { children?: ReactNode }) => <Code>{children}</Code>,
  table: ({ children }: { children?: ReactNode }) => (
    <ScrollArea type="auto" my="xs">
      <Table withTableBorder withColumnBorders>
        {children}
      </Table>
    </ScrollArea>
  ),
  thead: ({ children }: { children?: ReactNode }) => <Table.Thead>{children}</Table.Thead>,
  tbody: ({ children }: { children?: ReactNode }) => <Table.Tbody>{children}</Table.Tbody>,
  tr: ({ children }: { children?: ReactNode }) => <Table.Tr>{children}</Table.Tr>,
  th: ({ children }: { children?: ReactNode }) => <Table.Th>{children}</Table.Th>,
  td: ({ children }: { children?: ReactNode }) => <Table.Td>{children}</Table.Td>
};

export default function Markdown({
  children,
  components = {},
  slugHeadings = false
}: {
  children: string;
  components?: Components;
  /** Give every heading an id (e.g. "## Sources" -> #sources) via `rehype-slug`, so an
   * in-page anchor link can jump to it. Off by default: this component also renders
   * agent chat messages, and a page can show a report and the chat at once, so slugging
   * every surface risks two same-named headings racing for one id. Only a caller that
   * owns the only slugged surface on the page — the report — should opt in. */
  slugHeadings?: boolean;
}) {
  return (
    <Box className={styles.markdown}>
      {/* singleTilde: false — remark-gfm v4 defaults to true, which treats ~text~ as strikethrough.
          LLM-generated content uses single tildes for scientific notation etc., not markdown. */}
      <ReactMarkdown
        remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
        rehypePlugins={slugHeadings ? [rehypeSlug] : []}
        components={{ ...markdownComponents, ...components }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
